/**
 * Utility functions for managing isolated backtesting account.
 * The backtest balance is persisted to the database and synced
 * via the user object in localStorage (single source of truth).
 */

import { accountService } from "../services/api/accountService";
import { invalidateUserCache } from "./dataCache";
import { getJSON } from "./safeLocalStorage";

const DEFAULT_BACKTEST_ACCOUNT = {
  id: "backtest-fixed",
  name: "Backtesting Account",
  currentBalance: 10000
};

const getBacktestBalanceFromUser = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (typeof user.backtestBalance === "number") {
      return user.backtestBalance;
    }
  } catch (error) {
    console.error("Error reading backtest balance from user:", error);
  }
  return DEFAULT_BACKTEST_ACCOUNT.currentBalance;
};

const setBacktestBalanceInUser = (balance) => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    user.backtestBalance = parseFloat(balance);
    localStorage.setItem("user", JSON.stringify(user));
  } catch (error) {
    console.error("Error writing backtest balance to user:", error);
  }
};

export const getBacktestAccount = () => {
  return {
    ...DEFAULT_BACKTEST_ACCOUNT,
    currentBalance: getBacktestBalanceFromUser()
  };
};

export const updateBacktestBalance = async (newBalance) => {
  const updatedAccount = {
    ...DEFAULT_BACKTEST_ACCOUNT,
    currentBalance: parseFloat(newBalance)
  };

  // Update local user object immediately for fast UI
  setBacktestBalanceInUser(newBalance);

  // Persist to database for cross-device sync.
  // The API response also updates the user object in localStorage.
  try {
    await accountService.updateBacktestBalance(newBalance);
    
    // Invalidate cache so next page load gets updated user data
    const user = getJSON("user", {});
    if (user._id) {
      await invalidateUserCache(user._id);
    }
  } catch (error) {
    console.error("Failed to sync backtest balance to server:", error);
  }

  return updatedAccount;
};

export const getBacktestBalance = () => {
  return getBacktestBalanceFromUser();
};

export const resetBacktestAccount = async () => {
  setBacktestBalanceInUser(DEFAULT_BACKTEST_ACCOUNT.currentBalance);

  try {
    await accountService.updateBacktestBalance(DEFAULT_BACKTEST_ACCOUNT.currentBalance);
    
    // Invalidate cache so next page load gets updated user data
    const user = getJSON("user", {});
    if (user._id) {
      await invalidateUserCache(user._id);
    }
  } catch (error) {
    console.error("Failed to sync reset backtest balance to server:", error);
  }

  return { ...DEFAULT_BACKTEST_ACCOUNT };
};

export const validateBacktestAccount = (account) => {
  return (
    account &&
    typeof account.id === "string" &&
    typeof account.name === "string" &&
    typeof account.currentBalance === "number"
  );
};
